import { Checkbox, DialogContent, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ColorPicker, DraggableDialog, EnhancedTable, EnhancedTableCheckboxHead } from "../components";
import { useTableState } from "../hooks/use-table-state";
import { Asset, LineworkLayerData, updateModelLayer } from "../redux/assets-slice";

interface ModelLayerCheckboxProps {
  layerID: string;
  visible: boolean;
}

const ModelLayerCheckbox = (props: ModelLayerCheckboxProps) => {
  const {layerID, visible} = props;

  const dispatch = useDispatch();

  const onVisibilityChange = (visible: boolean) => {
    dispatch(updateModelLayer({
      layerID,
      visible
    }));
  };

  return (
    <Checkbox
      checked={visible}
      onChange={(event) => {
        const checked = event.target.checked;
        onVisibilityChange(checked);
      }}
    />
  );
};

interface ModelLayerColorProps {
  layerID: string;
  color: string;
}

const ModelLayerColor = (props: ModelLayerColorProps) => {
  const {layerID, color} = props;

  const dispatch = useDispatch();

  const onColorChange = (color: string) => {
    dispatch(updateModelLayer({
      layerID,
      color
    }));
  };

  return (
    <ColorPicker
      value={color}
      onSubmit={onColorChange}
    />
  );
};

interface ModelLayerDialogProps {
  open: boolean;
  onClose(): void;
  asset: Asset;
}

export const ModelLayerDialog = (props: ModelLayerDialogProps) => {
  const {open, onClose, asset} = props;

  const {t} = useTranslation();
  const dispatch = useDispatch();

  const tableState = useTableState({defaultSort: "name"});

  const layers = useMemo(() => {
    return asset.data as LineworkLayerData[];
  }, [asset.data]);

  const getRows = (layers: LineworkLayerData[]) => {
    return layers.map(layer => {
      return {
        id: `WebSourceLayerTableRow_${layer.id}`,
        name: layer.name,
        visible_sortable: layer.visible ? 1 : 0,
        visible_checkbox: <ModelLayerCheckbox
          layerID={layer.id}
          visible={layer.visible}
        />,
        color_picker: <ModelLayerColor
          layerID={layer.id}
          color={layer.color}
        />
      };
    });
  };

  const rows = useMemo(() => {
    return getRows(layers);
  }, [layers]);

  const columns = [
    {
      id: 'name',
      label: t('models.layer_name'),
      wordBreak: true,
      searchable: true
    },
    {
      id: 'color_picker',
      label: t('models.layer_color'),
      center: true,
      sortable: false
    },
    {
      id: 'visible_checkbox',
      label: <EnhancedTableCheckboxHead
        label={t('models.visibility')}
        values={layers.map(x => x.visible)}
        onChange={(checked) => {
          layers.forEach(layer => {
            dispatch(updateModelLayer({
              layerID: layer.id,
              visible: checked
            }));
          });
        }}
      />,
      sortBy: "visible_sortable",
      center: true
    }
  ];

  return (
    <React.Fragment>
      <DraggableDialog
        open={open}
        title={t('models.layer_manager')}
        onClose={onClose}
        initialWidth={"fit-content"}
      >
        <DialogContent style={{paddingBottom: "0px", flex: "none"}}>
          <Typography variant="caption">
            <b>{t('asset.file-name')}: </b> {asset.name}
          </Typography>
        </DialogContent>

        <DialogContent>
          <EnhancedTable
            state={tableState}
            columns={columns}
            rows={rows}
          />
        </DialogContent>

      </DraggableDialog>
    </React.Fragment>
  );
};
